/**
 * One check-in history list — shared by "my check-ins" (no reason needed) and the
 * optional "team check-ins" section, WP-W10. `web/shapes/geo.md` §5–§6.
 *
 * Presentational only, the same split `MyLeavePanel`'s balances region and
 * `LeaveApprovalsPanel` keep: the caller decides *which* read feeds it (own or
 * named), this component only renders the three outcomes of that read.
 *
 * ── The trust verdict, `showTrust` only (Pillar web, PW3) ────────────────────
 *
 * `TeamCheckinsPanel` is the only caller that sets `showTrust`. The verdict is a
 * NEUTRAL flag for a human reviewer, never an accusation and never wired to any
 * "reject"/"punish" affordance (`src/modules/geo/trust.ts`'s own research
 * guardrail) — so `trusted`, `suspect` and `unverified` all render at the same
 * `Badge` severity, `'info'`, and differ only by icon and words, never by a louder
 * colour. Reasons and the travel speed are additional plain facts beside it, not a
 * verdict of their own.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type { GeoCheckin, GeoTrust, GeoTrustReason } from '../../api/geo';
import type { MessageKey } from '../../i18n/messages';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { type IconName, SEVERITY_ICON } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';

/** Distinct silhouettes (DESIGN.md §4) at the one severity, `'info'` — the icon is
 * the only visual difference between the three verdicts. */
const TRUST_ICON: Readonly<Record<GeoTrust, IconName>> = {
  trusted: 'circle-check',
  suspect: 'circle-alert',
  unverified: 'info',
};

const TRUST_LABEL_KEY: Readonly<Record<GeoTrust, MessageKey>> = {
  trusted: 'geo.trust.trusted',
  suspect: 'geo.trust.suspect',
  unverified: 'geo.trust.unverified',
};

const TRUST_REASON_LABEL_KEY: Readonly<Record<GeoTrustReason, MessageKey>> = {
  mock_location: 'geo.trust.reason.mock_location',
  low_accuracy: 'geo.trust.reason.low_accuracy',
  no_accuracy: 'geo.trust.reason.no_accuracy',
  impossible_travel: 'geo.trust.reason.impossible_travel',
  location_opted_out: 'geo.trust.reason.location_opted_out',
};

export interface CheckinsListPanelProps {
  readonly status: QueryStatus;
  readonly checkins: readonly GeoCheckin[] | null;
  readonly error: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly subject: string;
  readonly emptyTitle: string;
  readonly emptyBody: string;
  readonly locale: string;
  readonly timezone: string;
  readonly testPrefix: string;
  /** The trust verdict column, `TeamCheckinsPanel` only. Omitted (or `false`) for
   * "my check-ins", which has no reason to flag its own reader's device to them. */
  readonly showTrust?: boolean;
}

export function CheckinsListPanel({
  status,
  checkins,
  error,
  onReload,
  subject,
  emptyTitle,
  emptyBody,
  locale,
  timezone,
  testPrefix,
  showTrust = false,
}: CheckinsListPanelProps): ReactNode {
  const { t } = useI18n();

  if (status === 'loading') {
    return <Skeleton rows={3} variant="block" label={subject} testId={`${testPrefix}-skeleton`} />;
  }
  if (status === 'error' && error !== null) {
    return (
      <ApiErrorNote
        error={error}
        onRetry={onReload}
        subject={subject}
        testId={`${testPrefix}-error`}
      />
    );
  }
  if (checkins === null) return null;
  if (checkins.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyBody} testId={`${testPrefix}-empty`} />;
  }

  return (
    <ul className="wp-rows" data-testid={`${testPrefix}-list`}>
      {checkins.map((checkin) => (
        <li className="wp-row" key={checkin.id} data-testid={`${testPrefix}-${checkin.id}`}>
          <div className="wp-row__main">
            <p>
              {/*
                Migration 055 — `within_geofence: null` is a THIRD state, not a
                falsy `false`: the employee has opted out of location capture, so
                this row carries no coordinates and there is no geofence verdict to
                report. It renders at severity `info` (never `medium`, which is what
                "outside the office" gets) because a person exercising a statutory
                right must not look, at a glance, like a person standing somewhere
                they should not be.
              */}
              {checkin.within_geofence === null ? (
                <Badge
                  severity="info"
                  icon={SEVERITY_ICON.info}
                  label={t('geo.badge.noLocation')}
                  testId={`${testPrefix}-${checkin.id}-badge`}
                />
              ) : (
                <Badge
                  severity={checkin.within_geofence ? 'low' : 'medium'}
                  icon={SEVERITY_ICON[checkin.within_geofence ? 'low' : 'medium']}
                  label={t(checkin.within_geofence ? 'geo.badge.within' : 'geo.badge.outside')}
                  testId={`${testPrefix}-${checkin.id}-badge`}
                />
              )}{' '}
              <span>
                {checkin.within_geofence === null
                  ? t('geo.row.noLocation')
                  : (checkin.office?.name ?? t('geo.row.noOffice'))}
              </span>
            </p>
            <p className="wp-small wp-muted">
              {formatInstant(checkin.checked_in_at, locale, timezone)}
              {checkin.distance_m === null
                ? ''
                : ` · ${t('geo.row.distance', { distance: Math.round(checkin.distance_m) })}`}
            </p>
            {showTrust ? (
              <p className="wp-small">
                <Badge
                  severity="info"
                  icon={TRUST_ICON[checkin.trust]}
                  label={t(TRUST_LABEL_KEY[checkin.trust])}
                  testId={`${testPrefix}-${checkin.id}-trust`}
                />
                {checkin.trust === 'trusted' || checkin.trust_reasons.length === 0 ? null : (
                  <span
                    className="wp-small wp-muted"
                    data-testid={`${testPrefix}-${checkin.id}-trust-reasons`}
                  >
                    {' '}
                    {checkin.trust_reasons
                      .map((reason) => t(TRUST_REASON_LABEL_KEY[reason]))
                      .join(', ')}
                  </span>
                )}
                {checkin.travel_speed_kmh === null ? null : (
                  <span
                    className="wp-small wp-muted"
                    data-testid={`${testPrefix}-${checkin.id}-trust-speed`}
                  >
                    {' '}
                    {t('geo.trust.speed', { speed: checkin.travel_speed_kmh })}
                  </span>
                )}
              </p>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}
